import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Chip,
  styled,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useView } from '../../utils/PersonalViewProvider';
import StatusChip from '../../utils/StatusChip';
import api from '../../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(1),
}));

const MetricCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  textAlign: 'center',
}));

const TaskHeaderRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: `2px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  fontWeight: 'bold',
  alignItems: 'center',
}));

const TaskRow = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1.5),
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AssigneeBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(0.5),
  border: `1px solid ${theme.palette.grey[300]}`,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  minHeight: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '120px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const AccuracyChip = styled(Chip)(({ accuracy }) => {
  let color = 'error';
  if (accuracy >= 80) color = 'success';
  else if (accuracy >= 50) color = 'warning';
  
  return {
    fontWeight: 'bold',
    minWidth: '60px',
    color: color,
  };
});

const AccuracyAnalysisReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentWorkspace } = useWorkspace();
  const { selectedAssignee } = useView();

  useEffect(() => {
    setLoading(true);
    const fetchReport = async () => {
      if (!currentWorkspace?.id) {
        setReportData(null);
        setLoading(false);
        return;
      }
      try {
        const params = {
          workspace_id: currentWorkspace.id,
          ...(selectedAssignee ? { assignee: selectedAssignee } : {}),
        };
        const response = await api.get('accuracy-analysis/', { params });
        setReportData(response.data.payload);
      } catch (error) {
        setReportData(null);
      }
      setLoading(false);
    };
    fetchReport();
  }, [currentWorkspace, selectedAssignee]);

  if (loading) return <div>Loading...</div>;

  if (!reportData) {
    return <Alert severity="info">No data available</Alert>;
  }

  const { overall_metrics: overallMetrics, tasks } = reportData;

  return (
    <Box sx={{ padding: 2 }}>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="primary">
                {overallMetrics.total_action_items}
              </Typography>
              <Typography variant="body2">Total Action Items</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="secondary">
                {overallMetrics.total_estimated_hours}h
              </Typography>
              <Typography variant="body2">Estimated Hours</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {overallMetrics.total_actual_hours}h
              </Typography>
              <Typography variant="body2">Actual Hours</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
        <Grid size={3}>
          <MetricCard>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {overallMetrics.overall_variance > 0 ? '+' : ''}{overallMetrics.overall_variance}h
              </Typography>
              <Typography variant="body2">Overall Variance</Typography>
            </CardContent>
          </MetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={4}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Overall Accuracy
              </Typography>
              <Typography variant="h4" color="primary">
                {overallMetrics.average_accuracy.toFixed(1)}%
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid size={4}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Accuracy Distribution
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Chip label="Highly Accurate" color="success" size="small" />
                  <Typography variant="body2">
                    {overallMetrics.accuracy_distribution.highly_accurate.count} ({overallMetrics.accuracy_distribution.highly_accurate.percentage}%)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Chip label="Moderately Accurate" color="warning" size="small" />
                  <Typography variant="body2">
                    {overallMetrics.accuracy_distribution.moderately_accurate.count} ({overallMetrics.accuracy_distribution.moderately_accurate.percentage}%)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Chip label="Inaccurate" color="error" size="small" />
                  <Typography variant="body2">
                    {overallMetrics.accuracy_distribution.inaccurate.count} ({overallMetrics.accuracy_distribution.inaccurate.percentage}%)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid size={4}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Estimation Bias
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Chip label="Overestimated" color="info" size="small" />
                  <Typography variant="body2">
                    {overallMetrics.estimation_bias.overestimated.count} ({overallMetrics.estimation_bias.overestimated.percentage}%)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Chip label="Underestimated" color="warning" size="small" />
                  <Typography variant="body2">
                    {overallMetrics.estimation_bias.underestimated.count} ({overallMetrics.estimation_bias.underestimated.percentage}%)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      <StyledCard>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Task Accuracy Details
          </Typography>
          
          <TaskHeaderRow container spacing={2}>
            <Grid size={3}>
              <Typography variant="caption">Task Name</Typography>
            </Grid>
            <Grid size={1.5}>
              <Typography variant="caption">Assignee</Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="caption">Status</Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="caption">Action Items</Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="caption">Estimated (h)</Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="caption">Actual (h)</Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="caption">Variance (h)</Typography>
            </Grid>
            <Grid size={1.5}>
              <Typography variant="caption">Accuracy (%)</Typography>
            </Grid>
          </TaskHeaderRow>

          {tasks.map((task) => (
            <TaskRow key={task.id} container spacing={2}>
              <Grid size={3}>
                <Typography variant="subtitle1">
                  {task.name || 'Unnamed Task'}
                </Typography>
              </Grid>
              <Grid size={1.5}>
                <AssigneeBox>{task.assignee || 'Unassigned'}</AssigneeBox>
              </Grid>
              <Grid size={1}>
                <StatusChip status={task.status || 'To Start'} />
              </Grid>
              <Grid size={1}>
                <Typography variant="body2">
                  {task.total_action_items}
                </Typography>
              </Grid>
              <Grid size={1}>
                <Typography variant="body2">
                  {task.total_estimated}
                </Typography>
              </Grid>
              <Grid size={1}>
                <Typography variant="body2">
                  {task.total_actual}
                </Typography>
              </Grid>
              <Grid size={1}>
                <Typography 
                  variant="body2"
                  color={task.task_variance > 0 ? 'error.main' : task.task_variance < 0 ? 'success.main' : 'text.primary'}
                >
                  {task.task_variance > 0 ? '+' : ''}{task.task_variance}
                </Typography>
              </Grid>
              <Grid size={1.5}>
                <AccuracyChip
                  label={`${task.task_accuracy >= 0 ? task.task_accuracy.toFixed(1) : 'N/A'}%`}
                  accuracy={task.task_accuracy}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </TaskRow>
          ))}
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default AccuracyAnalysisReport;